<template>
  <li
    class="accordion-group-value"
    :class="{ active: isExpanded, selected: isSelected }"
  >
    <div class="header">
      <button
        class="header-click-area"
        @click="toggleExpansion()"
      >
        <accordion-caret :is-open="isExpanded" />
        <span
          class="accordion-group-value-label"
          v-html="formattedLabel"
        />
      </button>
    </div>
    <div
      v-if="isExpanded"
      class="options row"
    >
      <div class="options-left col-9">
        <dl>
          <dt>Possible Categories</dt>
          <dd>
            <ul :class="useSubSelection && 'no-bullet-list'">
              <li
                v-for="(l, i) in allLevels"
                :key="l.order"
              >
                <template v-if="useSubSelection">
                  <input
                    type="checkbox"
                    :id="`checkbox-${i}`"
                    :name="i"
                    :checked="localSelectedLevels[i]"
                    @click="toggleLevel(i)"
                  >
                  <label
                    class="category-checkbox-label"
                    :for="`checkbox-${i}`"
                  >
                    {{ l.label }}
                  </label>
                </template>
                <template v-else>
                  {{ l.label }}
                </template>
              </li>
            </ul>

            <div v-if="useSubSelection">
              <button
                @click="selectAll"
                class="select-button"
              >
                Select All
              </button>
              <button
                @click="unselectAll"
                class="select-button"
              >
                Unselect All
              </button>
            </div>
          </dd>

          <dt>PUF Variable Name</dt>
          <dd>[{{ id }}]</dd>
          <dt>Applicable Population</dt>
          <dd>{{ applicablePopulation }}</dd>
        </dl>
      </div>
      <div class="options-right col-3">
        <button
          class="apply-button"
          :disabled="useSubSelection && (!hasMadeSelection || hasSelectedAll)"
          @click="onApply()"
        >
          Apply <i class="bi bi-check-circle-fill" />
        </button>
        <p
          v-if="useSubSelection && !hasMadeSelection"
          class="error-message"
        >
          Please select at least one category.
        </p>

        <p
          v-if="useSubSelection && hasSelectedAll"
          class="error-message"
        >
          Please unselect at least one category.
        </p>

        <p
          v-if="useSubSelection && wouldReplaceSelection && hasMadeSelection && !hasSelectedAll"
          class="warning-message"
        >
          This will replace the existing filter.
        </p>
      </div>
    </div>
  </li>
</template>

<script>
import AccordionCaret from './AccordionCaret.vue'
export default {
  components: { AccordionCaret },
  name: 'AccordionGroupValue',
  props: {
    id: {
      type: String,
      default: null
    },
    isSelected: {
      type: Boolean,
      default: null
    },
    wouldReplaceSelection: {
      type: Boolean,
      default: null
    },
    isExpanded: {
      type: Boolean,
      default: null
    },
    getValueProperties: {
      type: Function,
      default: null
    },
    useSubSelection: {
      type: Boolean,
      default: false
    },
    selectedLevels: {
      type: Object,
      default: null
    },
    searchString: {
      type: String,
      default: null
    }
  },
  data () {
    const allLevels = this.getValueProperties(this.id).levels
    const levelToggles = allLevels.map(al => {
      if (this.selectedLevels == null || this.selectedLevels.id !== this.id) {
        return !this.useSubSelection
      }

      return this.selectedLevels.levels.find(sl => sl.value === al.value) != null
    })

    return {
      allLevels,
      localSelectedLevels: levelToggles
    }
  },
  computed: {
    properties () {
      return this.getValueProperties(this.id)
    },
    label () {
      return this.properties.label || '---'
    },
    formattedLabel () {
      if (this.searchString == null || this.searchString.length === 0) {
        return this.label
      }

      const regex = new RegExp(this.searchString, 'gi')
      const label = this.label.replace(regex, '<span class="search-highlight">$&</span>')

      if (this.id.match(regex)) {
        return `[${this.id.replace(regex, '<span class="search-highlight">$&</span>')}] ${label}`
      } else {
        return label
      }
    },
    applicablePopulation () {
      return this.properties.pop
    },
    hasMadeSelection () {
      return this.localSelectedLevels.reduce((a, c) => a || c, false)
    },
    hasSelectedAll () {
      return this.localSelectedLevels.reduce((a, c) => a && c, true)
    }
  },
  methods: {
    toggleExpansion () {
      this.$emit('onToggleExpansion', { expand: !this.isExpanded })
    },
    onApply () {
      const levels = this.allLevels
        .filter((l, i) => this.localSelectedLevels[i])
        .map(l => l.value)

      this.$emit('onSelectQuestion', { levels })
    },
    toggleLevel (i) {
      const levels = [...this.localSelectedLevels]
      levels[i] = !levels[i]
      this.localSelectedLevels = levels
    },
    selectAll () {
      this.localSelectedLevels = this.localSelectedLevels.map(l => true)
    },
    unselectAll () {
      this.localSelectedLevels = this.localSelectedLevels.map(l => false)
    }
  },
  emits: ['onToggleExpansion', 'onSelectQuestion']
}
</script>

<style lang="scss" scoped>
.accordion-group-value {
  padding: 4px;
  padding-left: 12px;
  min-height: 1em;
  color: #575757;
  transition: background-color 400ms ease;
  position: relative;

  .header {
    display: flex;
    width: 100%;
    border-bottom: 1px dotted #aaa;

    .header-click-area {
      flex: 1 1 auto;
      display: flex;
      text-align: left;
      column-gap: 8px;
    }

    .accordion-caret {
      margin-top: 5px;
    }

    .accordion-group-value-label {
      font-size: 14px;
      font-weight: bold;
      flex: 1 1 auto;
    }

    button {
      border: 0px;
    }
  }

  &:hover {
 /* hrsa dashboard style guide color*/
    //background-color: #bed8ea;

    //paler alternate
    background-color: #edf1f7;
  }

  &.active {
    border: 2px solid #dce4ef;
    background-color: #edf1f7;

// this is blue sidebar indicator
    &:before {
      content: '';
      background-color: #69aed8;
      height: 100%;
      width: 6px;
      position: absolute;
      left: -2px;
      top: 0;
    }
  }

  &.selected {
    background-color: #edf1f7;

    .accordion-group-value-label {
      text-decoration: underline;
    }
  }

  .options {
    padding: 4px;
    padding-left: 2.5rem;
    background: #fff;

    .options-right {
      text-align: right;
    }

    .apply-button {
      flex: 0 0 auto;
      transition: 300ms ease all;
      margin-bottom: 4px;
      margin-left: 4px;
      padding-right: 0px;
      border: 0px;
      font-size:1em;
      background: none;
      color: #575757;

      i {
        color: #267cb8;
        transform: scale(1.2);
        transform-origin: left;
        display: inline-block;
        transition: 300ms ease all;
      }

      &:hover {
        i{
          color: #205492;
          transform: scale(1.45);
        }
      }

      &:disabled {
        color: #aaa;

        i {
          color: #aaa;
          transform: scale(1);
        }
      }
    }

    .error-message, .warning-message {
      font-style: italic;
      color: #981b1e;
      line-height: 1.2em;
      padding-right: 1.2em;
    }
  }
  .accordion-group.active .accordion-group-value.active:hover {
    background: none;
  }
  dt {
    font-weight: bold;
  }
  dt::after {
    content: ":";
  }
  dd {
    margin-left: 1rem;
  }
  dd > ul {
    list-style-position: inside;
    padding-left: 0;
  }

  .select-button {
    display: inline-block;
    transition: 300ms ease all;
    margin-bottom: 4px;
    margin-left: 4px;
    border: 0px;
    font-size:1em;
    background: #efefef;

    &:hover {
      background: #aaa;
    }
  }

  .no-bullet-list {
    list-style-type: none;
    padding: 0;
    margin: 0;

    li {
      display: flex;
      align-items: baseline;
    }
  }

  .category-checkbox-label {
    margin-bottom: 0;
    margin-left: 0.5rem;
  }
}
</style>

<style lang="scss">
.search-highlight {
  text-decoration: underline;
  background-color: #edf1f7;
}
</style>
